import { Edge, extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { useEffect, useState } from "react";

import { Action, zSourceData, zTargetData } from "./layoutEditState";

export function getNewIndex({
    targetParentId,
    sourceParentId,
    startIndex,
    closestEdgeOfTarget,
    indexOfTarget,
}: {
    targetParentId: string | null;
    sourceParentId: string | null;
    startIndex: number;
    closestEdgeOfTarget: Edge | null;
    indexOfTarget: number;
}): number {
    if (targetParentId !== sourceParentId) {
        return closestEdgeOfTarget === "top" ? indexOfTarget : indexOfTarget + 1;
    }

    // Make dropping before the first node work
    if (indexOfTarget === -1) {
        return 0;
    }

    return getReorderDestinationIndex({
        startIndex,
        closestEdgeOfTarget: closestEdgeOfTarget ?? "bottom",
        indexOfTarget,
        axis: "vertical",
    });
}

export function useDragAndDrop(dispatch: (action: Action) => void) {
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        return monitorForElements({
            canMonitor: () => true,
            onDragStart() {
                setIsDragging(true);
            },
            onDrop({ location, source }) {
                setIsDragging(false);
                const dropTargets = location.current.dropTargets.map(target => ({
                    raw: target.data,
                    parsed: zTargetData.parse(target.data),
                }));

                dropTargets.sort((a, b) => b.parsed.depth - a.parsed.depth);
                const targetData = dropTargets[0];
                if (!targetData) {
                    return;
                }

                const sourceData = zSourceData.parse(source.data);

                if (targetData.parsed.t === "delete") {
                    dispatch({
                        type: "delete-block",
                        blockId: sourceData.block.id,
                    });
                    return;
                }

                const closestEdge = extractClosestEdge(targetData.raw);

                if (
                    sourceData.parentId === targetData.parsed.parentId &&
                    sourceData.index === targetData.parsed.index
                ) {
                    return;
                }

                const newIndex = getNewIndex({
                    startIndex: sourceData.index,
                    closestEdgeOfTarget: closestEdge,
                    indexOfTarget: targetData.parsed.index,
                    targetParentId: targetData.parsed.parentId,
                    sourceParentId: sourceData.parentId,
                });

                dispatch({
                    type: "move-block",
                    sourceParentId: sourceData.parentId,
                    targetParentId: targetData.parsed.parentId,
                    sourceIndex: sourceData.index,
                    newIndex,
                });
            },
        });
    }, [dispatch]);

    return { isDragging };
}
